import { lazy, type ComponentType } from 'react';

function isLazyChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError/i.test(message);
}

function lazyWithRetry<T extends ComponentType<Record<string, unknown>>>(
  factory: () => Promise<{ default: T }>,
  storageKey: string
) {
  return lazy(async () => {
    try {
      const module = await factory();
      try { sessionStorage.removeItem(storageKey); } catch {
        /* sessionStorage may be unavailable in private browsing. */
      }
      return module;
    } catch (error) {
      if (isLazyChunkError(error)) {
        try {
          if (!sessionStorage.getItem(storageKey)) {
            sessionStorage.setItem(storageKey, '1');
            window.location.reload();
          }
        } catch {
          window.location.reload();
        }
      }
      throw error;
    }
  });
}

/** Lazy geladene Seiten — reduziert initiales Bundle; gemeinsam mit build.manualChunks. */
export const LazyGoogleAdsSection = lazyWithRetry(() => import('@/components/GoogleAds/GoogleAdsSection'), 'lazy-retry-google-ads');
export const LazyGuidePage = lazyWithRetry(() => import('@/components/Guide/GuidePage'), 'lazy-retry-guide');
export const LazyInsightsPage = lazyWithRetry(() => import('@/components/Insights/InsightsPage'), 'lazy-retry-insights');
export const LazyProductsPage = lazyWithRetry(() => import('@/components/Products/ProductsPage'), 'lazy-retry-products');
export const LazyMaterialPage = lazyWithRetry(() => import('@/components/Material/MaterialPage'), 'lazy-retry-material');
export const LazyStreamPage = lazyWithRetry(() => import('@/components/Stream/StreamPage'), 'lazy-retry-stream');
export const LazyDocumentsPage = lazyWithRetry(() => import('@/components/Documents/DocumentsPage'), 'lazy-retry-documents');
export const LazyAutomatePage = lazyWithRetry(() => import('@/components/Automate/AutomatePage'), 'lazy-retry-automate');
export const LazyClosingAssistant = lazyWithRetry(() => import('@/components/Automate/ClosingAssistant'), 'lazy-retry-closing-assistant');
export const LazyBrandBookPage = lazyWithRetry(() => import('@/components/BrandBook/BrandBookPage'), 'lazy-retry-brandbook');
export const LazyCustomerIntelligencePage = lazyWithRetry(
  () => import('@/components/CustomerIntelligence/CustomerIntelligencePage'),
  'lazy-retry-customer-intelligence'
);
export const LazyEmailsPage = lazyWithRetry(() => import('@/components/Emails/EmailsPage'), 'lazy-retry-emails');
export const LazyBusinessPlanPage = lazyWithRetry(() => import('@/components/BusinessPlan/BusinessPlanPage'), 'lazy-retry-business-plan');
export const LazyTimeTrackingPage = lazyWithRetry(() => import('@/components/TimeTracking/TimeTrackingPage'), 'lazy-retry-time-tracking');
export const LazyEngagementsPage = lazyWithRetry(() => import('@/components/Engagements/EngagementsPage'), 'lazy-retry-engagements');
export const LazyExpensesPage = lazyWithRetry(() => import('@/components/Expenses/ExpensesPage'), 'lazy-retry-expenses');
export const LazyTeamSkillsPage = lazyWithRetry(() => import('@/components/TeamSkills/TeamSkillsPage'), 'lazy-retry-team-skills');
export const LazyCompanyProfilePage = lazyWithRetry(() => import('@/components/Company/CompanyProfilePage'), 'lazy-retry-company-profile');
export const LazyUIShowcase = lazyWithRetry(() => import('@/components/UIShowcase'), 'lazy-retry-ui-showcase');
export const LazyMeetingPrepPage = lazyWithRetry(() => import('@/components/MeetingPrep/MeetingPrepPage'), 'lazy-retry-meeting-prep');
export const LazyPublicAuditView = lazyWithRetry(() => import('@/components/Public/PublicAuditView'), 'lazy-retry-public-audit');
export const LazyFeedbackPage = lazyWithRetry(() => import('@/components/Feedback/FeedbackPage'), 'lazy-retry-feedback');
export const LazySoftwareCompliancePage = lazyWithRetry(() => import('@/components/SoftwareCompliance/SoftwareCompliancePage'), 'lazy-retry-software-compliance');
export const LazyCompaniesPage = lazyWithRetry(() => import('@/components/Companies/CompaniesPage'), 'lazy-retry-companies');
export const LazyCustomersPage = lazyWithRetry(() => import('@/components/Customers/CustomersPage'), 'lazy-retry-customers');
export const LazyOpportunityList = lazyWithRetry(() => import('@/components/Opportunities/OpportunityListNew'), 'lazy-retry-opportunities');
export const LazySettingsPage = lazyWithRetry(() => import('@/components/Settings/SettingsPage'), 'lazy-retry-settings');
export const LazyLeadsList = lazyWithRetry(() => import('@/components/Leads/LeadsList'), 'lazy-retry-leads');
export const LazyTasksListModern = lazyWithRetry(() => import('@/components/Tasks/TasksListModern'), 'lazy-retry-tasks');
export const LazyDevelopmentPage = lazyWithRetry(() => import('@/components/Development/DevelopmentPage'), 'lazy-retry-development');
